import { FC, SyntheticEvent, useCallback, useEffect, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import clsx from "clsx";

import {
  StyledTab,
  StyledTabLabelContainer,
  StyledTabMarker,
  StyledTabs,
} from "styles/components/tabs";

type TabProps = {
  label: string;
  value: string;
  disabled?: boolean;
  active?: boolean;
};

interface TabsProps {
  value?: string;
  tabs: TabProps[];
  blue?: boolean;
  className?: string;
  dataTestId?: string;
  scrollButtons?: "auto" | boolean;

  onChange?: (value: string) => void;
}

const initialTabs: TabProps[] = [
  { label: "First tab", value: "first" },
  { label: "Second tab", value: "second" },
  { label: "Third tab", value: "third" },
];

const Tabs: FC<TabsProps> = ({
  value,
  tabs = initialTabs,
  blue,
  className,
  dataTestId,
  scrollButtons,
  onChange,
}) => {
  const theme = useTheme();

  const [selectedTab, setSelectedTab] = useState<string>(
    value ?? tabs[0].value
  );

  useEffect(() => {
    if (value) {
      setSelectedTab(value);
    }
  }, [value]);

  const tabsClassName = clsx(["tabs", { tabs_blue: blue }, className]);

  const handleChange = useCallback(
    (event: SyntheticEvent, value: string) => {
      setSelectedTab(value);
      onChange?.(value);
    },
    [onChange]
  );

  const renderTab = useCallback(
    (value: string, label: string, active?: boolean, disabled?: boolean) => {
      const renderedTabLabel = (
        <StyledTabLabelContainer className="tab__label-container">
          <Typography
            position="relative"
            fontWeight={300}
            textTransform="none"
            variant="h3"
            color={theme.palette.common.black}
            className="tab__label"
            data-test-id={dataTestId && `${dataTestId}-label`}
          >
            {label}
            {active && (
              <StyledTabMarker
                data-test-id={dataTestId && `${dataTestId}-marker`}
              />
            )}
          </Typography>
        </StyledTabLabelContainer>
      );

      return (
        <StyledTab
          key={value}
          value={value}
          label={renderedTabLabel}
          disableRipple
          className="tab"
          color={blue ? theme.palette.blue.main : theme.palette.primary.main}
          disabled={disabled}
          data-test-id={dataTestId && `${dataTestId}-tab`}
        />
      );
    },
    [blue, theme, dataTestId]
  );

  return (
    <StyledTabs
      value={selectedTab}
      onChange={handleChange}
      className={tabsClassName}
      data-test-id={dataTestId}
      variant="scrollable"
      scrollButtons={scrollButtons}
    >
      {tabs.map(({ value, label, active, disabled }) =>
        renderTab(value, label, active, disabled)
      )}
    </StyledTabs>
  );
};

export default Tabs;
